import { toast } from "@/components/common/Toast/Toast"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect, useRef, useState } from "react"
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router"
import {
  useDeclarationRow,
  useDeclarationsWithPolling,
  useHydrateDeclarationInterface,
} from "../../../api/declarationsApi"
import { useOnRecompute } from "../../../api/recomputeSignal"
import type { LifecycleStatus } from "../../../api/types"
import { listParamsFromSearch } from "../../declarations/listParams"
import { StudioV2Page } from "../../declarations/review/studio-v2/StudioV2Page"
import {
  useRefetchOnSettled,
  useRunPoll,
} from "../../declarations/review/studio/useRunPoll"

const DECLARATIONS_INBOX = "/material-compliance/supplier-studio/declarations"
/** C2 — how far the fast-open page pages the list in the background, looking for this
 *  declaration's place in it (Next). The list no longer gates anything, so a
 *  declaration outside the current filter must not crawl the whole inbox. */
const MAX_BACKGROUND_PAGES = 5

/**
 * THE EXTRACTION HAS NOT FINISHED — the two lifecycle states in which the flow's every
 * interface-backed read is empty AND will fill itself in without the reviewer doing
 * anything. `pending` is queued, `processing` is walking; both are waits with an end,
 * which is the whole distinction the screens below draw on.
 */
const isAwaitingExtraction = (status: LifecycleStatus | undefined): boolean =>
  status === "pending" || status === "processing"

/**
 * The declaration review page: the review studio (`StudioV2Page`) for one declaration,
 * behind the guards every opening shares (no id, not found, failed extraction). The URL
 * contract is the studio's own (`studioV2Url.ts`).
 *
 * ## THE ROUTE OPENS WHILE THE DECLARATION IS STILL PROCESSING
 *
 * It used to send a `processing` declaration straight back to the inbox, which meant
 * the one thing a reviewer most wants to see — their document being read — was the one
 * thing the product refused to show. The machinery to show it was already here (the
 * poll, the live derivations, the board's ghost matrix); only this gate stood in the
 * way. Every lifecycle state is now answered on its own terms:
 *
 *   `pending` / `processing`   the flow, landing on OVERVIEW, whose live board
 *                              builds as the run walks. Steps 1–3 stay addressable and
 *                              read as being determined, never as broken or empty.
 *   `ready` / `accepted` /     the flow, unchanged — the reviewer's ordinary entry.
 *   `expiring` / legacy
 *   `failed`                   said out loud. A failed extraction produces no
 *                              interface and never will, so spinning on one is a lie
 *                              with no end.
 *   not in the list at all     the inbox, as before.
 *
 * AND IT HYDRATES IN PLACE WHEN THE RUN LANDS. Every non-poll read on this page was
 * fetched mid-run and is empty; `useHydrateDeclarationInterface` refetches them the
 * moment the declaration's lifecycle leaves `processing`, without remounting anything.
 * See that hook for why the run feed's own terminal event is the wrong trigger.
 */
export function DeclarationReviewPage() {
  const { id } = useParams<{ id: string }>()
  const fastOpen = useFeatureFlag("MC_REVIEW_FAST_OPEN")
  // C2 — a separate component, not a branch inside one: the two paths call different
  // hooks, and the flag can settle after the first render.
  if (id && fastOpen) return <FastOpenReviewPage key={id} id={id} />
  return <ListReviewPage />
}

/** The review page as it opens today: from the declarations list, paged until the
 *  row is found. */
function ListReviewPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { translate } = useTranslation()
  // `listParamsFromSearch` (TUR-1211) — the review route used to fetch the
  // list with NO params, so the server's own no-sort default (`ORDER BY id`,
  // i.e. insertion order) decided skip order rather than whatever the
  // reviewer had filtered/sorted on the overview. `params` here already
  // carries those list keys: `DeclarationsModule`'s row click appends them,
  // and they survive every navigation this page and the studio make (see
  // `listSearchString`'s own callers).
  const { data, status, fetchNextPage, hasNextPage, isFetchingNextPage } =
    // Every row, held copies included (TUR-1754): a timeline or notification link
    // must still open one, even though the inbox leaves them out.
    useDeclarationsWithPolling({
      ...listParamsFromSearch(params),
      held: undefined,
    })
  const declarations = data?.pages.flatMap((page) => page.items)
  const declaration = declarations?.find((d) => d.id === id)

  const stillSearching = !declaration && !!hasNextPage
  useEffect(() => {
    if (stillSearching && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [stillSearching, isFetchingNextPage, fetchNextPage])

  const awaitingExtraction = isAwaitingExtraction(declaration?.lifecycleStatus)
  useReviewHydration(id ?? "", awaitingExtraction)

  const navigateBack = () => navigate(DECLARATIONS_INBOX)

  // Guard: declarations loaded, and this one is genuinely not among them.
  // Was a SILENT redirect back to the inbox — a stale bookmark or a shared
  // link to a since-deleted declaration landed the reviewer back where they
  // started with no explanation at all. `useEffect`, not fired inline during
  // render, so it toasts exactly once (on the render that first observes the
  // not-found state) rather than on every re-render before `<Navigate>`
  // actually unmounts this component. Only a list that LOADED can say the
  // declaration is missing: a failed load (a tab waking before the network is
  // back) is no list at all, and the list keeps polling while it has no data.
  const notFound =
    !!id && status === "success" && !stillSearching && !declaration
  useEffect(() => {
    if (notFound) toast.error(translate("MC_DECL_V2_NOT_FOUND"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound])

  if (!id) {
    return <Navigate to={DECLARATIONS_INBOX} replace />
  }

  if (status === "pending" || stillSearching || (!declaration && !notFound)) {
    return <div className="flex h-full items-center justify-center bg-card" />
  }

  if (!declaration) {
    return <Navigate to={DECLARATIONS_INBOX} replace />
  }

  // A failed extraction has no interface and will never grow one. Say so.
  if (declaration.lifecycleStatus === "failed") {
    return <ExtractionFailedView onBack={navigateBack} />
  }

  return (
    <StudioV2Page
      key={id}
      declId={id}
      supplierName={declaration.supplier}
      declarations={declarations ?? []}
      awaitingExtraction={awaitingExtraction}
    />
  )
}

/**
 * The page's two refresh signals, shared by both ways of opening it. `awaitingExtraction`
 * comes from whichever row the page holds (the list's, or its own `GET /{id}`), which
 * polls every 3 s while the run walks.
 */
function useReviewHydration(id: string, awaitingExtraction: boolean) {
  // HYDRATION, ON THE ONE SIGNAL THAT CANNOT BE EARLY. The row this page holds (the
  // list's, or its own `GET /{id}`) polls every 3 s while the run walks, and the
  // lifecycle it carries flips in
  // the same commit that writes the interface — so a transition OUT of the awaiting
  // states is exactly "the reads are answerable now". Latched, so it fires once and
  // never on a declaration that was already processed when the page opened.
  const hydrate = useHydrateDeclarationInterface(id)
  const wasAwaiting = useRef(false)
  useEffect(() => {
    if (awaitingExtraction) {
      wasAwaiting.current = true
      return
    }
    if (wasAwaiting.current) {
      wasAwaiting.current = false
      hydrate()
    }
  }, [awaitingExtraction, hydrate])

  // AND ON EVERY SETTLED CONE. The hydration above is the once-per-run arrival
  // signal; this is the recurring one. A reviewer's confirm fires a DETACHED
  // recompute that re-publishes the interface minutes later, and the gesture's own
  // invalidation can only name the reads it knew about — a cone reaches further
  // than that by definition. `recompute_settled` is the pipeline saying it has
  // finished publishing, so the same seven interface-backed reads are marked stale
  // there too, and the re-published values land without a remount.
  //
  // THE FEED IS ARMED BY THE GESTURE, never on mount: a poll only sees a settle while
  // it runs, and running it permanently would re-ship the whole node matrix every
  // 1.2 s. Every mutation that starts a cone announces it (`announceRecompute`), and
  // `watch()` keeps the loop alive while the cone is open, then winds it down after
  // the grace. A second gesture restarts the one loop rather than adding another.
  const runFeed = useRunPoll(id)
  useOnRecompute(id, runFeed.watch)
  useRefetchOnSettled(runFeed.settledCount, hydrate)
}

/**
 * C2 — the review page opened from the declaration's OWN row (`GET /{id}`), one call,
 * instead of paging the list until it finds the row. Every guard the list gave is
 * taken from that row: not found (404) → the inbox with the same toast; failed → said
 * out loud; the awaiting latch → its lifecycle. The list still loads, in the
 * background and capped, only to know what Next is; it never blocks the page.
 */
function FastOpenReviewPage({ id }: { id: string }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { translate } = useTranslation()
  const row = useDeclarationRow(id)
  const declaration = row.data

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useDeclarationsWithPolling({
      ...listParamsFromSearch(params),
      held: undefined,
    })
  const declarations = data?.pages.flatMap((page) => page.items) ?? []
  // Latched: once found, the list stays usable for Next even if a later refetch of a
  // lifecycle-filtered list drops this row (an approve moves it out of the filter).
  const foundNow = declarations.some((d) => d.id === id)
  const [foundBefore, setFoundBefore] = useState(false)
  if (foundNow && !foundBefore) setFoundBefore(true)
  const inList = foundNow || foundBefore
  const keepPaging =
    !inList && !!hasNextPage && (data?.pages.length ?? 0) < MAX_BACKGROUND_PAGES
  useEffect(() => {
    if (keepPaging && !isFetchingNextPage) fetchNextPage()
  }, [keepPaging, isFetchingNextPage, fetchNextPage])

  const awaitingExtraction = isAwaitingExtraction(declaration?.lifecycleStatus)
  useReviewHydration(id, awaitingExtraction)

  const notFound =
    row.isError &&
    (row.error as { response?: { status?: number } })?.response?.status === 404
  useEffect(() => {
    if (notFound) toast.error(translate("MC_DECL_V2_NOT_FOUND"))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFound])

  const navigateBack = () => navigate(DECLARATIONS_INBOX)

  if (notFound) return <Navigate to={DECLARATIONS_INBOX} replace />
  if (!declaration) {
    if (row.isError)
      return (
        <RowLoadErrorView onBack={navigateBack} onRetry={() => row.refetch()} />
      )
    return <div className="flex h-full items-center justify-center bg-card" />
  }
  if (declaration.lifecycleStatus === "failed") {
    return <ExtractionFailedView onBack={navigateBack} />
  }
  return (
    <StudioV2Page
      declId={id}
      supplierName={declaration.supplier}
      declaration={declaration}
      // Next is read off the list only once this row is in it; before that there
      // is no honest "next" to offer.
      declarations={inList ? declarations : []}
      awaitingExtraction={awaitingExtraction}
    />
  )
}

/** The row could not be loaded (not a 404): say so, offer a retry. */
function RowLoadErrorView({
  onBack,
  onRetry,
}: {
  onBack: () => void
  onRetry: () => void
}) {
  const { translate } = useTranslation()
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 bg-card px-6 text-center">
      <p className="text-[13px] text-grey5">
        {translate("MC_DECL_V2_LOAD_ERROR")}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="sv2-btn sv2-btn-sm sv2-btn-ghost"
        >
          {translate("MC_DECL_FAILED_BACK")}
        </button>
        <button
          type="button"
          onClick={onRetry}
          className="sv2-btn sv2-btn-sm sv2-btn-primary"
        >
          {translate("MC_DECL_BTN_RETRY")}
        </button>
      </div>
    </div>
  )
}

/**
 * The extraction failed — the honest end of the road. There is no interface to review
 * and no run still walking, so every other surface on this page would be a spinner that
 * never resolves. Retrying lives in the inbox, beside the row that failed.
 */
function ExtractionFailedView({ onBack }: { onBack: () => void }) {
  const { translate } = useTranslation()
  return (
    <div className="flex h-full items-center justify-center bg-card px-6">
      <div className="max-w-[420px] text-center">
        <p className="text-sm font-semibold text-white">
          {translate("MC_DECL_FAILED_TITLE")}
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-grey5">
          {translate("MC_DECL_FAILED_BODY")}
        </p>
        <button
          type="button"
          onClick={onBack}
          className="mt-5 rounded-md border border-grey4 bg-white/4 px-4 py-1.5 text-[12px] font-medium text-grey5 transition-colors hover:bg-white/8 hover:text-white"
        >
          {translate("MC_DECL_FAILED_BACK")}
        </button>
      </div>
    </div>
  )
}
